import { v4 as uuid } from "uuid";
import { z } from "zod";
import { EmployeeSocialFactor, EmploymentLevel, Gender, Occupation, OccupationType } from "../../entities/contractorEmployee";
import { SocialCommitmentTypeHour } from "../packageLabourHourCommitment";
import { LabourHourFactorCountingMethod, ProjectLabourHourCommitmentType, LabourHourTargetDenominator } from "./enums";
import { ProjectCommmitmentGenerationSource } from "../project/enums";
import { getLocationBasedLabourHourDefaults, LOCAL_JOBS_FIRST_DISTANCE } from "../project/projectLocationDefaults";
export { LOCAL_JOBS_FIRST_DISTANCE };
// Re-export enums for backward compatibility
export { LabourHourFactorCountingMethod, ProjectLabourHourCommitmentType, LabourHourTargetDenominator };
export const projectLabourHourCommitmentTypeToString = (targetType) => {
    switch (targetType) {
        case ProjectLabourHourCommitmentType.Fixed:
            return "Fixed Target";
        case ProjectLabourHourCommitmentType.Ratio:
            return "Ratio Target";
        case ProjectLabourHourCommitmentType.Headcount:
            return "Headcount";
        case ProjectLabourHourCommitmentType.HeadcountRatio:
            return "Headcount Ratio";
        case ProjectLabourHourCommitmentType.TotalHours:
            return "Total Hours";
        case ProjectLabourHourCommitmentType.TotalHeadcount:
            return "Total Headcount";
    }
};
export const projectLabourHourCommitmentTypeToMetricLabel = (targetType) => {
    switch (targetType) {
        case ProjectLabourHourCommitmentType.Fixed:
            return "Hours";
        case ProjectLabourHourCommitmentType.Ratio:
            return "%";
        case ProjectLabourHourCommitmentType.Headcount:
            return "Headcount";
        case ProjectLabourHourCommitmentType.HeadcountRatio:
            return "Headcount %";
        case ProjectLabourHourCommitmentType.TotalHours:
            return "Hours";
        case ProjectLabourHourCommitmentType.TotalHeadcount:
            return "Headcount";
    }
};
export const labourHourTargetDenominatorToString = (targetDenominator) => {
    switch (targetDenominator) {
        case LabourHourTargetDenominator.TotalWorkHours:
            return "Total Work Hours";
        // Deliberately BEP-free: these denominators are just the estimated hours entered at
        // project setup, and the Building Equality Policy is Victoria-only, so the label
        // misread as VIC-specific everywhere else. Matches the enum identifiers, and the
        // setup inputs that feed them ("Trade Hours (Estimated)" et al). The enum *values*
        // still contain `total-bep-hours-*` — they are persisted, so renaming them would
        // need a data migration and is not worth it for a display change.
        case LabourHourTargetDenominator.TotalHoursTrade:
            return "Total Hours Trade";
        case LabourHourTargetDenominator.TotalHoursNonTrade:
            return "Total Hours Non-Trade";
        case LabourHourTargetDenominator.TotalHoursManagement:
            return "Total Hours Management";
        case LabourHourTargetDenominator.ACTHours:
            return "ATC Hours";
        case LabourHourTargetDenominator.ProjectTrainingTargetHours:
            return "Project Training Target Hours";
        default:
            return "Unknown Denominator";
    }
};
export const labourHourFactorCountingMethodToString = (factorCountingMethod) => {
    switch (factorCountingMethod) {
        case LabourHourFactorCountingMethod.AnyMatchingFactor:
            return "Any matching factor";
        case LabourHourFactorCountingMethod.AllFactorsCombined:
        default:
            return "All factors combined";
    }
};
export const newEntrantToConstructionToString = (newEntrantToConstruction) => {
    return newEntrantToConstruction ? "New Entrant" : "Existing Worker";
};
/**
 * One CRITERIA SET of a labour hours Target Definition: the Employee-filtering half,
 * and nothing else.
 *
 * A Target Definition matches an Employee when ANY of its criteria sets does, each
 * criteria set's own filters combining by its own `factorCountingMethod`. That is the
 * gap `factorCountingMethod` cannot close alone: it ORs across FIELD TYPES
 * ("female OR apprentice"), never across sets — "(female AND within 50km) OR
 * (apprentice AND any distance)" needs two criteria sets.
 *
 * Deliberately NOT carrying `targetValue`, `targetMetricType`,
 * `targetDenominator`, `colour` or `includeArchivedEmployees`: those stay on the
 * commitment, which is what makes two criteria sets two ways to qualify for ONE
 * target rather than two targets. `appliedTargetIds` still receives a single id
 * per Target Definition, so every rollup is untouched and an Employee matching
 * both criteria sets counts once.
 *
 * Structurally satisfies {@link EmployeeFilters}, which is what lets the
 * existing matcher evaluate a criteria set with no changes to its per-filter rules.
 */
export const labourHourCommitmentCriteriaSetSchema = z.object({
    abns: z.array(z.string()).optional(),
    employmentLevel: z.array(z.enum(EmploymentLevel)).optional(),
    excludedPostCodes: z.array(z.string()).optional(),
    factorCountingMethod: z.enum(LabourHourFactorCountingMethod).optional(),
    gender: z.array(z.enum(Gender)).optional(),
    id: z.string(),
    maxDistance: z.number().optional(),
    newEntrantToConstruction: z.boolean().optional(),
    occupation: z.array(z.enum(Occupation)).optional(),
    occupationType: z.array(z.enum(OccupationType)).optional(),
    /** Tab order in the authoring form. 0-based, unique within a commitment. */
    ordinal: z.number(),
    postCodes: z.array(z.string()).optional(),
    socialFactors: z.array(z.enum(EmployeeSocialFactor)).optional()
});
export const projectLabourHourCommitmentLineItemSchema = z.object({
    abns: z.array(z.string()).optional(),
    colour: z.string(),
    created: z.date(),
    /**
     * The criteria sets that qualify an Employee for this target, ORed together.
     *
     * Optional and possibly EMPTY, which means "fall back to the flat filter
     * fields on this object". Every commitment gets one criteria set by backfill, so an
     * empty array in practice means a row written by an older build — see
     * `resolveCriteriaSets`, the one place that resolves the fallback.
     */
    criteriaSets: z.array(labourHourCommitmentCriteriaSetSchema).optional(),
    deleted: z.boolean().optional(),
    employmentLevel: z.array(z.enum(EmploymentLevel)).optional(),
    excludedPostCodes: z.array(z.string()).optional(),
    factorCountingMethod: z.enum(LabourHourFactorCountingMethod).optional(),
    gender: z.array(z.enum(Gender)).optional(),
    generationSource: z.enum(ProjectCommmitmentGenerationSource).optional(),
    id: z.string(),
    includeArchivedEmployees: z.boolean().optional(),
    maxDistance: z.number().optional(),
    newEntrantToConstruction: z.boolean().optional(),
    occupation: z.array(z.enum(Occupation)).optional(),
    occupationType: z.array(z.enum(OccupationType)).optional(),
    postCodes: z.array(z.string()).optional(),
    projectId: z.string(),
    socialFactors: z.array(z.enum(EmployeeSocialFactor)).optional(),
    sourceFrameworkId: z.string().optional(),
    sourceFrameworkTargetId: z.string().optional(),
    targetDenominator: z.enum(LabourHourTargetDenominator),
    targetDescription: z.string().optional(),
    targetMetricType: z.enum(ProjectLabourHourCommitmentType),
    targetName: z.enum(SocialCommitmentTypeHour),
    targetValue: z.number(),
    targetValueRealised: z.number()
});
export const createDefaultLabourHourCommitmentsForProject = (projectId, country, state) => {
    // Get location-specific defaults, or null if not found
    const locationDefaults = getLocationBasedLabourHourDefaults(country, state);
    // If no location defaults found, return empty array
    if (!locationDefaults) {
        return [];
    }
    // Create commitments from location-specific defaults
    return Object.values(SocialCommitmentTypeHour).reduce((acc, sc) => {
        if (sc !== SocialCommitmentTypeHour.DefaultHour &&
            sc !== SocialCommitmentTypeHour.GeespApprentice) {
            const commitmentConfiguration = locationDefaults[sc];
            if (commitmentConfiguration) {
                return [
                    ...acc,
                    {
                        ...commitmentConfiguration,
                        achievedValue: 0,
                        created: new Date(),
                        factorCountingMethod: commitmentConfiguration?.factorCountingMethod ??
                            LabourHourFactorCountingMethod.AllFactorsCombined,
                        generationSource: ProjectCommmitmentGenerationSource.Custom,
                        id: uuid(),
                        includeArchivedEmployees: commitmentConfiguration?.includeArchivedEmployees ?? true,
                        projectId,
                        targetDenominator: commitmentConfiguration?.targetDenominator ??
                            LabourHourTargetDenominator.TotalWorkHours,
                        targetMetricType: commitmentConfiguration.targetMetricType ??
                            ProjectLabourHourCommitmentType.Fixed,
                        targetName: sc,
                        targetValueRealised: 0
                    }
                ];
            }
        }
        return [...acc];
    }, []);
};
export const createDefaultLabourHourCommitment = (projectId) => {
    return {
        achievedValue: 0,
        colour: "#000000",
        created: new Date(),
        employmentLevel: [],
        factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
        gender: [],
        generationSource: ProjectCommmitmentGenerationSource.Custom,
        id: uuid(),
        includeArchivedEmployees: true,
        maxDistance: undefined,
        newEntrantToConstruction: undefined,
        projectId,
        projectSocialSpendCommitmentId: uuid(),
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetName: SocialCommitmentTypeHour.DefaultHour,
        targetValue: 0,
        targetValueRealised: 0
    };
};
export const getBaseMultiplierForHours = (financial, c) => {
    // Use targetDenominator to determine the base multiplier
    // PackageLabourHourCommitmentLineItem doesn't have targetDenominator, so default to TotalWorkHours
    const targetDenominator = "targetDenominator" in c
        ? c.targetDenominator
        : LabourHourTargetDenominator.TotalWorkHours;
    switch (targetDenominator) {
        case LabourHourTargetDenominator.TotalHoursTrade:
            return financial.estimatedBepHoursTrade ?? 0;
        case LabourHourTargetDenominator.TotalHoursNonTrade:
            return financial.estimatedBepHoursNonTrade ?? 0;
        case LabourHourTargetDenominator.TotalHoursManagement:
            return financial.estimatedBepHoursManagement ?? 0;
        case LabourHourTargetDenominator.ACTHours:
            return financial.estimatedACTHours ?? 0;
        case LabourHourTargetDenominator.TotalWorkHours:
        default:
            return financial.estimatedTotalWorkHours ?? 0;
    }
};
